import { useState, useEffect } from "react";
import { Box, Typography, Skeleton, Tabs, Tab } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import UsersTable from "./components/tables/UsersTable";
import SiteUsersTable from "./components/tables/SiteUsersTable";
import CreateUserButton from "./components/modals/CreateUserButton";
import CreateUserModal from "./components/modals/CreateUserModal";
import {
    selectUsers,
    selectSiteUsers,
    setUsers,
    selectRoles,
    setRoles,
    selectTrialsUserManagement,
    setTrialsUserManagement,
    selectSiteRoles,
} from "./userManagementSlice";
import { useGetAvailableRolesQuery, useGetTrialsUserManagementQuery, useGetUsersQuery } from "./dataAccess";
import useRoles from "../permissions/useRoles";
import countriesList from "./constants/Countries";

const UserManagement = () => {
    const dispatch = useDispatch();
    const [modalOpen, setModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [tabValue, setTabValue] = useState(0);

    const users = useSelector(selectUsers) ?? [];
    const siteUsers = useSelector(selectSiteUsers) ?? [];
    const roles = useSelector(selectRoles) ?? [];
    const siteRoles = useSelector(selectSiteRoles) ?? [];
    const trials = useSelector(selectTrialsUserManagement) ?? [];
    const { userActiveRole } = useRoles();

    const { data: usersData, isLoading: usersLoading, error: usersError } = useGetUsersQuery();
    const { data: rolesData, isLoading: rolesLoading, error: rolesError } = useGetAvailableRolesQuery();
    const { data: trialsData, isLoading: trialsLoading, error: trialsError } = useGetTrialsUserManagementQuery();

    useEffect(() => {
        const compositeLoading = usersLoading || rolesLoading || trialsLoading;
        setIsLoading(compositeLoading);

        if (
            usersData &&
            typeof usersData === "object" &&
            "usersFromSites" in usersData &&
            "usersFromTrials" in usersData
        ) {
            dispatch(setUsers(usersData));
        }

        if (trialsData && Array.isArray(trialsData)) {
            dispatch(setTrialsUserManagement(trialsData));
        }

        if (rolesData && Array.isArray(rolesData)) {
            const availableRoles =
                userActiveRole === "Client Admin"
                    ? rolesData.filter((role) => role !== "Master Admin" && role !== "Client Admin")
                    : rolesData.filter((role) => role !== "Master Admin");
            dispatch(setRoles(availableRoles));
        }
    }, [
        usersData,
        usersError,
        usersLoading,
        rolesData,
        rolesError,
        rolesLoading,
        trialsData,
        trialsError,
        trialsLoading,
        dispatch,
    ]);

    const handleCreateUser = () => setModalOpen(true);
    const handleCloseModal = () => setModalOpen(false);
    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    return (
        <Box sx={{ width: "100%", minHeight: "100vh", py: 4, px: { xs: 1, sm: 2, md: 3 } }}>
            <CreateUserModal
                open={modalOpen}
                onClose={handleCloseModal}
                rolesList={roles}
                siteRolesList={siteRoles}
                countriesList={countriesList}
                trialsList={trials}
                initialTab={tabValue}
            />
            <Box mb={4} p={11}>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    flexDirection={{ xs: "column", sm: "row" }}
                    gap={{ xs: 2, sm: 0 }}
                    mb={1}
                >
                    <Typography variant="h5" fontWeight="bold">
                        User Administration
                    </Typography>
                    <CreateUserButton onClick={handleCreateUser} />
                </Box>
                <Typography variant="body1" color="textSecondary">
                    Manage regular users and site users
                </Typography>
            </Box>

            {isLoading ? (
                <>
                    <Box mb={6}>
                        <Skeleton variant="rectangular" width="100%" height={60} sx={{ mb: 2, borderRadius: 1 }} />
                        {[...Array(10)].map((_, index) => (
                            <Skeleton
                                key={index}
                                variant="rectangular"
                                width="100%"
                                height={45}
                                sx={{ mb: 1, borderRadius: 1 }}
                            />
                        ))}
                    </Box>
                </>
            ) : (
                <>
                    <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3, ml: 5 }}>
                        <Tabs value={tabValue} onChange={handleTabChange} aria-label="user management tabs">
                            <Tab label="Users" />
                            <Tab label="Site Users" />
                        </Tabs>
                    </Box>

                    {tabValue === 0 && (
                        <Box mb={6}>
                            <UsersTable users={users} totalCount={users.length} />
                        </Box>
                    )}

                    {tabValue === 1 && (
                        <Box>
                            <SiteUsersTable users={siteUsers} totalCount={siteUsers.length} />
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default UserManagement;
